import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Button from '../../../external/components/Button/Button';
import {
  StyledDd,
  StyledDefWrapper,
  StyledDl,
  StyledDt,
} from '../../../external/components/DefLists';
import { Skeleton } from '../../../external/components/SkeletonLoading/Skeleton';
import Text from '../../../external/components/Text/Text';
import { State } from '../../../reducers';
import { NetworkId } from '../../../types/network.types';
import { ButtonContainer } from '../components/ButtonContainer';
import { HorizontalRule } from '../components/HorizontalRule';
import { BigNumberHelper } from '../helpers/BigNumbeHelper';
import { getNetworkText } from '../helpers/getNetworkText';
import { useUnstakeAvailable } from '../hooks/useCalculateStakeAvailable';
import { useNakedStakingPool } from '../hooks/usePool';
import { useStakingData } from '../hooks/useStakingData';
import { StakingScreenEnum } from './_Screens';

interface Props {
  walletNetworkId: NetworkId;
  setScreen: React.Dispatch<React.SetStateAction<StakingScreenEnum>>;
}

export function ViewCurrentStake({ walletNetworkId, setScreen }: Props) {
  const pool = useNakedStakingPool();
  const walletAddress = useSelector(({ account }: State) => ({
    walletAddress: account.address,
  }));

  const {
    stakingTokenSymbol,
    tokenBalance: tokenBalanceStore,
    stakedBalance,
    estimatedDailyReward,
    finished,
  } = pool;

  const {
    unstakeData: poolUnstakeData,
    loading,
    tokenBalance: tokenBalanceFetched,
    earnedRewardsLoading,
    getEarnedRewards,
    getTokenBalance,
    setLoading,
    getUnstakes,
  } = useStakingData({
    pool,
  });

  const unstakeData = pool.unstakeData.amount > 0 ? pool.unstakeData : poolUnstakeData;

  const { timeUntil, isAvailable } = useUnstakeAvailable(
    unstakeData.unstakingDuration,
    parseFloat(unstakeData.applicableAt),
  );

  const tokenBalance = tokenBalanceStore || tokenBalanceFetched;

  const stakedBalanceLessThanOrZero = BigNumberHelper.isLessThanOrEqualToZero(stakedBalance || '0');

  async function init() {
    setLoading(true);
    try {
      if (pool.unstakeData.amount <= 0) {
        await getUnstakes();
      }
      if (!pool.tokenBalance) {
        await getTokenBalance();
      }
      if (!pool.rewardsEarned) {
        await getEarnedRewards();
      }
    } catch (error) {
      //@fix here
    }
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, []);
  return (
    <>
      <StyledDl>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current {pool.stakingTokenSymbol} balance
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='100px' dark={true} />
              ) : (
                <>
                  <Text dark={true} as='span'>
                    {BigNumberHelper.formatBigNumb(tokenBalance || '0')} {stakingTokenSymbol}
                  </Text>
                </>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Current amount staked
            </Text>
          </StyledDt>
          <StyledDd>
            <>
              {loading ? (
                <Skeleton height='18px' width='200px' dark={true} />
              ) : (
                <>
                  <Text dark={true} as='span'>
                    {BigNumberHelper.formatBigNumb(stakedBalance || '0')} {stakingTokenSymbol}
                  </Text>
                  <Text color='green' as='span'>
                    {' '}
                    (${parseFloat(pool.stakedBalanceUsd)} USD)
                  </Text>
                </>
              )}
            </>
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Total rewards earned
            </Text>
          </StyledDt>
          <StyledDd>
            {' '}
            {loading || earnedRewardsLoading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {BigNumberHelper.formatBigNumb(pool.rewardsEarned || 0) || 0} {stakingTokenSymbol}
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Estimated daily rewards
            </Text>
          </StyledDt>
          <StyledDd>
            {loading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {BigNumberHelper.formatBigNumb(estimatedDailyReward || 0)} {stakingTokenSymbol}
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
        <StyledDefWrapper>
          <StyledDt>
            <Text dark={true} as='span' weight={600}>
              Estimated Apr
            </Text>
          </StyledDt>
          <StyledDd>
            {loading ? (
              <Skeleton height='18px' width='100px' dark={true} />
            ) : (
              <>
                <Text dark={true} as='span'>
                  {pool.apr} %
                </Text>
              </>
            )}
          </StyledDd>
        </StyledDefWrapper>
      </StyledDl>

      {walletNetworkId !== pool.networkId && (
        <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
          {' '}
          To start staking/unstaking in this {pool.stakingTokenSymbol} staking pool, please connect
          to the {getNetworkText(pool.networkId)} network
        </Text>
      )}

      {loading ? (
        <ButtonContainer>
          <Skeleton height='36px' width='100px' dark={true} />
          <Skeleton height='36px' width='100px' dark={true} />
        </ButtonContainer>
      ) : (
        <>
          <ButtonContainer>
            {!finished ? (
              <Button
                dark={true}
                onClick={() => setScreen(StakingScreenEnum.STAKE_SCREEN)}
                disabled={walletNetworkId !== pool.networkId}
              >
                {!stakedBalanceLessThanOrZero ? 'Stake more' : 'Stake'}
              </Button>
            ) : null}
            {!stakedBalanceLessThanOrZero && (
              <Button
                dark={true}
                variant='secondary'
                disabled={!pool.stakedBalance || walletNetworkId !== pool.networkId}
                onClick={() => setScreen(StakingScreenEnum.UNSTAKE_SCREEN)}
              >
                Unstake
              </Button>
            )}
          </ButtonContainer>
        </>
      )}

      <StyledDl>
        <>
          {unstakeData.amount > 0 && (
            <>
              <HorizontalRule />
              <br />
              <StyledDefWrapper>
                <StyledDt>
                  <Text dark={true} as='span' weight={600}>
                    {!isAvailable ? (
                      <>Unstaking {stakingTokenSymbol}</>
                    ) : (
                      <>Available {stakingTokenSymbol} for withdrawal</>
                    )}
                  </Text>
                </StyledDt>
                <StyledDd>
                  <div>
                    <Text dark={true} as='span'>
                      {unstakeData.amount.toLocaleString('en-US', { maximumFractionDigits: 6 })}{' '}
                      {stakingTokenSymbol}
                    </Text>
                    <Text color='green' as='span'>
                      {' '}
                      ({unstakeData.usdAmount} USD)
                    </Text>
                    <Text color='green' style={{ margin: '0.5rem 0' }}>
                      {`Available ${isAvailable ? 'now for withdrawal' : ` in ${timeUntil}`}`}
                    </Text>
                    {walletNetworkId !== pool.networkId && (
                      <Text dark={true} size='sm' style={{ margin: '0.5rem 0' }}>
                        {' '}
                        To withdraw, please connect to the {getNetworkText(pool.networkId)} network
                      </Text>
                    )}

                    <Button
                      variant='secondary'
                      dark={true}
                      onClick={() => setScreen(StakingScreenEnum.WITHDRAW_CONFIRM)}
                      disabled={!isAvailable || walletNetworkId !== pool.networkId}
                    >
                      Withdraw token
                    </Button>
                  </div>
                </StyledDd>
              </StyledDefWrapper>
            </>
          )}
        </>
      </StyledDl>
      <>
        {walletAddress && (
          <>
            <HorizontalRule />
            <Text dark={true} weight={600}>
              Transaction history
            </Text>
            <Button
              dark={true}
              disabled={loading}
              variant='secondary'
              onClick={() => setScreen(StakingScreenEnum.TRANSACTION_HISTORY)}
            >
              View transaction history
            </Button>
          </>
        )}
      </>
    </>
  );
}
